<template>
  <div class="col-12 col-xl-12 ">
    <div
      class="card mb-3 mb-xxl-5 me-xxl-4"
      style="margin-top: 4rem; overflow: visible"
    >
      <div
        class="card-header p-0 position-relative mt-n4 mx-3 z-index-2"
        style="height: 60px"
      >
        <div
          class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100"
        >
          <h6 class="text-white text-capitalize ps-3 pe-3">{{ $t('assignments.tableTitle') }}</h6>
        </div>
      </div>

      <div class="card-body px-0 pb-0 pb-2 pe-xxl-3">
        <div class="table-responsive p-1 p-xxl-4">
          <LoadingSpinner
            v-if="loading && assignments.length === 0"
            :message="$t('assignments.loadingMessage')"
            class="py-3 py-5"
          />

          <template v-else>
            <div class="d-block d-xxl-none">
              <div
                v-if="!loading && assignments.length === 0"
                class="text-center py-3 text-muted"
              >
                <i class="fas fa-book fa-2x mb-2"></i>
                <p>{{ $t('assignments.noAssignments') }}</p>
              </div>

              <div
                v-for="assignment in filteredAssignments"
                :key="'mobile-' + assignment.id"
                class="card mb-3 mx-2"
              >
                <div class="card-body py-2">
                  <div class="d-flex align-items-center justify-content-between">
                    <h6 class="mb-0 text-sm">{{ assignment.title }}</h6>
                    <span
                      class="badge badge-sm"
                      :class="isOverdue(assignment.due_date) ? 'bg-danger' : 'bg-success'"
                    >
                      {{ assignment.formatted_due_date }}
                    </span>
                  </div>

                  <p class="text-xs text-muted mb-1 mt-2" v-if="assignment.description">
                    {{ truncate(assignment.description, 50) }}
                  </p>

                  <div class="d-flex justify-content-between align-items-center mt-2">
                    <div>
                      <span class="badge badge-sm bg-info me-1">{{ assignment.class_name }}</span>
                      <span class="badge badge-sm bg-secondary me-1">{{ assignment.group_name }}</span>
                      <span class="badge badge-sm bg-warning me-1">{{ assignment.subject_name }}</span>
                    </div>

                    <div class="d-inline-flex">
                      <router-link
                        :to="{ name: 'assignments.submissions', params: { assignmentId: assignment.id } }"
                        class="btn btn-link text-info p-0 mx-1"
                        :title="$t('assignments.viewSubmissions')"
                      >
                        <i class="fas fa-file-import fa-xs"></i>
                      </router-link>
                      <router-link
                        :to="{ name: 'assignments.edit', params: { id: assignment.id } }"
                        class="btn btn-link text-secondary p-0 mx-1"
                        :title="$t('assignments.edit')"
                      >
                        <i class="fas fa-edit fa-xs"></i>
                      </router-link>
                      <button
                        @click="openDeleteDialog(assignment)"
                        class="btn btn-link text-danger p-0 mx-1"
                        :title="$t('assignments.delete')"
                        :disabled="isProcessing"
                      >
                        <i class="fas fa-trash fa-xs"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <table class="table align-items-center mb-0 d-none d-xxl-table">
              <thead>
                <tr>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('assignments.columns.title') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('assignments.columns.class') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('assignments.columns.group') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('assignments.columns.subject') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('assignments.columns.teacher') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('assignments.columns.dueDate') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center pe-4">{{ $t('assignments.columns.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!loading && assignments.length === 0">
                  <td colspan="12" class="text-center py-4 text-muted">
                    <i class="fas fa-book fa-2x mb-2"></i>
                    <p>{{ $t('assignments.noAssignments') }}</p>
                  </td>
                </tr>

                <template v-if="!loading">
                  <tr v-for="assignment in filteredAssignments" :key="assignment.id">
                    <td>
                      <div class="d-flex px-3 py-1">
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-md">{{ assignment.title }}</h6>
                          <p class="text-xs text-muted mb-0" v-if="assignment.description">
                            {{ truncate(assignment.description, 50) }}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td><p class="text-xs font-weight-bold mb-0">{{ assignment.class_name }}</p></td>
                    <td><p class="text-xs font-weight-bold mb-0">{{ assignment.group_name }}</p></td>
                    <td><p class="text-xs font-weight-bold mb-0">{{ assignment.subject_name }}</p></td>
                    <td><p class="text-xs font-weight-bold mb-0">{{ assignment.teacher_name }}</p></td>
                    <td>
                      <span class="badge badge-sm" :class="isOverdue(assignment.due_date) ? 'bg-danger' : 'bg-success'">
                        {{ assignment.formatted_due_date }}
                      </span>
                    </td>
                    <td class="align-middle text-end pe-4">
                      <div class="d-inline-flex">
                        <router-link
                          :to="{ name: 'assignments.submissions', params: { assignmentId: assignment.id } }"
                          class="btn btn-link text-info p-1 mx-1"
                          :title="$t('assignments.viewSubmissions')"
                        >
                          <i class="fas fa-file-import fa-sm"></i>
                        </router-link>
                        <router-link
                          :to="{ name: 'assignments.edit', params: { id: assignment.id } }"
                          class="btn btn-link text-secondary p-1 mx-1"
                          :title="$t('assignments.edit')"
                        >
                          <i class="fas fa-edit fa-sm"></i>
                        </router-link>
                        <button
                          @click="openDeleteDialog(assignment)"
                          class="btn btn-link text-danger p-1 mx-1"
                          :title="$t('assignments.delete')"
                          :disabled="isProcessing"
                        >
                          <i class="fas fa-trash fa-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, computed } from "vue";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import { useStore } from "vuex";

const store = useStore();
const currentUser = computed(() => store.state.user);
const isTeacher = computed(() => currentUser.value?.role?.slug === "teacher");
const props = defineProps({
  assignments: {
    type: Array,
    required: true,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  teacherFilter: {
    type: Boolean,
    default: false,
  },
});

const isProcessing = ref(false);
const emit = defineEmits(["delete-assignment"]);

const truncate = (text, length) => text.length > length ? text.substring(0, length) + "..." : text;

const isOverdue = (dueDate) => new Date(dueDate) < new Date();

const openDeleteDialog = (assignment) => emit("delete-assignment", assignment);

const filteredAssignments = computed(() => {
  if (isTeacher.value) {
    return props.assignments.filter(a => a.teacher_id === currentUser.value.id);
  }
  return props.assignments;
});
</script>
